import { useConfig } from '@exam-management/configuration';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogTitle,
  FormRadioWithIconSelection,
  Form,
  DialogContent,
  DialogActions,
  Button,
} from '@shared/components';
import { type ElementType, useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { QConfigSummaryDetails } from '../q-config-summary-details';

import {
  MAPPED_SHOULD_DISPLAY_ANSWER_OPTIONS_ICON,
  MAPPED_SHOULD_SORT_BY_EXAM_SETTINGS_OPTIONS_ICON,
  MAPPED_WITH_TIMER_OPTIONS_ICON,
  SHOULD_DISPLAY_ANSWER_OPTIONS,
  SHOULD_SORT_BY_EXAM_SETTINGS_OPTIONS,
  WITH_TIMER_OPTIONS,
} from './constants';
import {
  ExamRoomPreviewSettingsFormSchema,
  type ExamRoomPreviewSettingsFormInput,
} from './schemas';

const defaultValues: ExamRoomPreviewSettingsFormInput = {
  withTimer: 'true',
  shouldSortByExamSettings: 'true',
  shouldDisplayAnswer: 'false',
};

export interface ExamRoomPreviewSettingsFormModalProps {
  open: boolean;
  onClose: () => void;
  subtaskId: string;
}

export default function ExamRoomPreviewSettingsFormModal(
  props: Readonly<ExamRoomPreviewSettingsFormModalProps>,
) {
  const { open: isOpen, onClose, subtaskId } = props;

  const {
    examRoom: { previewUrl: examRoomPreviewUrl },
  } = useConfig();

  const formContext = useForm<ExamRoomPreviewSettingsFormInput>({
    defaultValues,
    resolver: zodResolver(ExamRoomPreviewSettingsFormSchema),
  });

  const getOptionsWithIcons = useCallback(
    (options: { label: string; value: string }[], iconMap: Record<string, ElementType>) => {
      return options.map((option) => {
        const IconComponent = iconMap[option.value];
        return {
          ...option,
          icon: <IconComponent />,
        };
      });
    },
    [],
  );

  const withTimerOptions = useMemo(
    () => getOptionsWithIcons(WITH_TIMER_OPTIONS, MAPPED_WITH_TIMER_OPTIONS_ICON),
    [getOptionsWithIcons],
  );
  const shouldSortByExamSettingsOptions = useMemo(
    () =>
      getOptionsWithIcons(
        SHOULD_SORT_BY_EXAM_SETTINGS_OPTIONS,
        MAPPED_SHOULD_SORT_BY_EXAM_SETTINGS_OPTIONS_ICON,
      ),
    [getOptionsWithIcons],
  );
  const shouldDisplayAnswerOptions = useMemo(
    () =>
      getOptionsWithIcons(SHOULD_DISPLAY_ANSWER_OPTIONS, MAPPED_SHOULD_DISPLAY_ANSWER_OPTIONS_ICON),
    [getOptionsWithIcons],
  );

  const handleSubmit = ({
    withTimer,
    shouldSortByExamSettings,
    shouldDisplayAnswer,
  }: ExamRoomPreviewSettingsFormInput) => {
    const previewUrl = new URL(`${examRoomPreviewUrl}/task/${subtaskId}/instruction`);
    previewUrl.search = new URLSearchParams({
      withTimer,
      shouldSortByExamSettings,
      shouldDisplayAnswer,
    }).toString();

    window.open(previewUrl.toString(), '_blank');
    handleClose();
  };

  const handleClose = () => {
    onClose();
  };

  const handleModalExited = () => {
    formContext.reset(defaultValues);
  };

  return (
    <FormProvider {...formContext}>
      <Dialog
        maxWidth='sm'
        slotProps={{
          paper: {
            component: Form,
            onSubmit: formContext.handleSubmit(handleSubmit),
          },
          transition: { onExited: handleModalExited },
        }}
        open={isOpen}
        onClose={handleClose}
      >
        <DialogTitle onClose={handleClose}>Exam Room Preview</DialogTitle>
        <DialogContent
          dividers
          sx={(theme) => ({ display: 'flex', flexDirection: 'column', gap: theme.spacing(4) })}
        >
          <QConfigSummaryDetails subtaskId={subtaskId} />
          <FormRadioWithIconSelection
            label='การจับเวลาสอบ'
            name='withTimer'
            row
            options={withTimerOptions}
            slotProps={{ optionWrapper: { sx: { flex: 1 } } }}
          />
          <FormRadioWithIconSelection
            label='การเรียงลำดับคำถาม'
            name='shouldSortByExamSettings'
            row
            options={shouldSortByExamSettingsOptions}
            slotProps={{ optionWrapper: { sx: { flex: 1 } } }}
          />
          <FormRadioWithIconSelection
            label='การแสดงเฉลย'
            name='shouldDisplayAnswer'
            row
            options={shouldDisplayAnswerOptions}
            slotProps={{ optionWrapper: { sx: { flex: 1 } } }}
          />
        </DialogContent>
        <DialogActions>
          <Button size='large' onClick={handleClose} color='secondary' variant='outlined'>
            Cancel
          </Button>
          <Button size='large' type='submit' color='primary' variant='contained'>
            Start Preview
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
